import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container, Table, Button, Badge, Card, Row, Col, Form,
    InputGroup, Spinner, Alert, Dropdown, OverlayTrigger, Tooltip
} from 'react-bootstrap';
import {
    collection, getDocs, query, where, orderBy, doc, updateDoc,
    deleteDoc, getFirestore, serverTimestamp, addDoc
} from 'firebase/firestore';
import firebaseApp from '../../firebase/credenciales';
import {
    FaBriefcase, FaPlus, FaSearch, FaFilter, FaEllipsisV,
    FaEdit, FaTrashAlt, FaEye, FaMapMarkerAlt, FaCalendarAlt,
    FaUserTie, FaInfoCircle
} from 'react-icons/fa';
import Swal from 'sweetalert2';

const VacantesListScreen = ({ usuario }) => {
    const navigate = useNavigate();
    const db = getFirestore(firebaseApp);
    
    // Estados para gestionar las vacantes y filtros
    const [vacantes, setVacantes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filtros, setFiltros] = useState({
        estado: 'todas',
        area: '',
        busqueda: ''
    });
    const [showFiltros, setShowFiltros] = useState(false);
    const [areas, setAreas] = useState([]);
    
    // Obtener RUC de la empresa del usuario autenticado
    const ruc = usuario?.ruc || '';
    const userRole = usuario?.rol || 'usuario';
    const userEmail = usuario?.email || '';
    
    // Cargar vacantes al iniciar el componente
    useEffect(() => {
        const cargarVacantes = async () => {
            setLoading(true);
            try {
                await cargarAreas();
                await obtenerVacantes();
            } catch (error) {
                console.error("Error cargando vacantes:", error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'No se pudieron cargar las vacantes. Por favor, inténtelo nuevamente.'
                });
            } finally {
                setLoading(false);
            }
        };
        
        cargarVacantes();
    }, [ruc]);
    
    // Función para cargar áreas de la empresa
    const cargarAreas = async () => {
        try {
            const areasRef = collection(db, 'areas');
            const q = query(areasRef, where('ruc', '==', ruc), orderBy('nombre'));
            const snapshot = await getDocs(q);

            const areasList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setAreas(areasList);
        } catch (error) {
            console.error("Error cargando áreas:", error);
        }
    };

    // Función para obtener vacantes según los filtros
    const obtenerVacantes = async () => {
        try {
            const vacantesRef = collection(db, 'rs_vacantes');
            
            // Construir query base
            let q = query(vacantesRef, where('ruc', '==', ruc));
            
            // Aplicar filtro por estado
            if (filtros.estado !== 'todas') {
                q = query(q, where('estado', '==', filtros.estado));
            }
            
            // Obtener resultados y ordenar por fecha de creación (más recientes primero)
            q = query(q, orderBy('fechaCreacion', 'desc'));
            
            const snapshot = await getDocs(q);
            
            let vacantesList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            
            // Filtrar por área si se seleccionó una
            if (filtros.area) {
                vacantesList = vacantesList.filter(vacante => 
                    vacante.area === filtros.area
                );
            }
            
            // Filtrar por término de búsqueda
            if (filtros.busqueda) {
                const busquedaLower = filtros.busqueda.toLowerCase();
                vacantesList = vacantesList.filter(vacante => 
                    vacante.titulo?.toLowerCase().includes(busquedaLower) ||
                    vacante.descripcion?.toLowerCase().includes(busquedaLower) ||
                    vacante.ciudad?.toLowerCase().includes(busquedaLower)
                );
            }
            
            setVacantes(vacantesList);
            
        } catch (error) {
            console.error("Error obteniendo vacantes:", error);
            throw error;
        }
    };

    // Función para manejar cambios en los filtros
    const handleFiltroChange = (campo, valor) => {
        setFiltros(prev => ({
            ...prev,
            [campo]: valor
        }));
    };

    // Aplicar filtros al hacer click en el botón
    const aplicarFiltros = async () => {
        setLoading(true);
        try {
            await obtenerVacantes();
        } catch (error) {
            console.error("Error aplicando filtros:", error);
        } finally {
            setLoading(false);
        }
    };

    // Reiniciar filtros
    const limpiarFiltros = () => {
        setFiltros({
            estado: 'todas',
            area: '',
            busqueda: ''
        });
        
        // Aplicar filtros limpios
        setTimeout(() => {
            aplicarFiltros();
        }, 100);
    };

    // Función para cambiar el estado de una vacante (activar/desactivar)
    const cambiarEstadoVacante = async (vacanteId, estadoActual) => {
        try {
            const nuevoEstado = estadoActual === 'activa' ? 'inactiva' : 'activa';
            
            await updateDoc(doc(db, 'rs_vacantes', vacanteId), {
                estado: nuevoEstado,
                fechaActualizacion: serverTimestamp(),
                actualizadoPor: {
                    email: userEmail,
                    rol: userRole
                }
            });
            
            // Registrar cambio en historial
            await addDoc(collection(db, 'rs_vacantes_historial'), {
                vacanteId: vacanteId,
                accion: 'cambio_estado',
                fecha: serverTimestamp(),
                usuario: userEmail,
                detalles: `Cambio de estado de "${estadoActual}" a "${nuevoEstado}"`
            });
            
            // Actualizar la lista de vacantes
            await obtenerVacantes();
            
            Swal.fire({
                icon: 'success',
                title: 'Estado actualizado',
                text: `La vacante ha sido ${nuevoEstado === 'activa' ? 'activada' : 'desactivada'} correctamente.`,
                timer: 2000,
                showConfirmButton: false
            });
            
        } catch (error) {
            console.error("Error cambiando estado de vacante:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo actualizar el estado de la vacante.'
            });
        }
    };

    // Función para eliminar una vacante
    const eliminarVacante = async (vacanteId, titulo) => {
        // Pedir confirmación al usuario
        const result = await Swal.fire({
            title: '¿Está seguro?',
            text: `¿Desea eliminar la vacante "${titulo}"? Esta acción no se puede deshacer.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });
        
        if (result.isConfirmed) {
            try {
                // Eliminar la vacante
                await deleteDoc(doc(db, 'rs_vacantes', vacanteId));
                
                // Registrar en historial
                await addDoc(collection(db, 'rs_vacantes_historial'), {
                    vacanteId: vacanteId,
                    accion: 'eliminacion',
                    fecha: serverTimestamp(),
                    usuario: userEmail,
                    detalles: `Eliminación de la vacante "${titulo}"`
                });
                
                // Actualizar la lista
                await obtenerVacantes();
                
                Swal.fire({
                    icon: 'success',
                    title: 'Eliminada',
                    text: 'La vacante ha sido eliminada correctamente.',
                    timer: 2000,
                    showConfirmButton: false
                });
                
            } catch (error) {
                console.error("Error eliminando vacante:", error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'No se pudo eliminar la vacante. Por favor, inténtelo nuevamente.'
                });
            }
        }
    };

    // Formatear fecha para mostrar
    const formatearFecha = (timestamp) => {
        if (!timestamp) return "N/A";
        
        try {
            const fecha = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
            return fecha.toLocaleDateString('es-ES', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch (error) {
            return "Fecha inválida";
        }
    };

    // Renderizar panel de filtros
    const renderFiltros = () => (
        <Card className="mb-4">
            <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Filtros de búsqueda</h5>
                <Button 
                    variant="link" 
                    className="p-0 text-muted" 
                    onClick={() => setShowFiltros(!showFiltros)}
                >
                    {showFiltros ? 'Ocultar' : 'Mostrar'}
                </Button>
            </Card.Header>
            
            {showFiltros && (
                <Card.Body>
                    <Row>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Estado</Form.Label>
                                <Form.Select 
                                    value={filtros.estado}
                                    onChange={(e) => handleFiltroChange('estado', e.target.value)}
                                >
                                    <option value="todas">Todas</option>
                                    <option value="activa">Activas</option>
                                    <option value="inactiva">Inactivas</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Área</Form.Label>
                                <Form.Select 
                                    value={filtros.area}
                                    onChange={(e) => handleFiltroChange('area', e.target.value)}
                                >
                                    <option value="">Todas las áreas</option>
                                    {areas.map((area) => (
                                        <option key={area.id} value={area.nombre}>
                                            {area.nombre}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Buscar</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type="text"
                                        placeholder="Título, descripción..."
                                        value={filtros.busqueda}
                                        onChange={(e) => handleFiltroChange('busqueda', e.target.value)}
                                    />
                                    <Button variant="outline-secondary">
                                        <FaSearch />
                                    </Button>
                                </InputGroup>
                            </Form.Group>
                        </Col>
                    </Row>
                    
                    <div className="d-flex justify-content-end gap-2">
                        <Button variant="outline-secondary" onClick={limpiarFiltros}>
                            Limpiar filtros
                        </Button>
                        <Button variant="primary" onClick={aplicarFiltros}>
                            Aplicar filtros
                        </Button>
                    </div>
                </Card.Body>
            )}
        </Card>
    );

    // Renderizar la tabla de vacantes
    const renderTablaVacantes = () => {
        if (loading) {
            return (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2">Cargando vacantes...</p>
                </div>
            );
        }
        
        if (vacantes.length === 0) {
            return (
                <Alert variant="info">
                    <FaInfoCircle className="me-2" />
                    No se encontraron vacantes con los filtros seleccionados. 
                    <Button 
                        variant="link" 
                        className="p-0 ms-2"
                        onClick={() => navigate('/vacantes/crear')}
                    >
                        Crear una nueva vacante
                    </Button>
                </Alert>
            );
        }
        
        return (
            <Table responsive hover className="align-middle">
                <thead className="bg-light">
                    <tr>
                        <th>Título / Área</th>
                        <th>Ubicación</th>
                        <th>Fechas</th>
                        <th>Estado</th>
                        <th>Postulaciones</th>
                        <th>Acciones</th>
                    </tr>
                </thead>
                <tbody>
                    {vacantes.map((vacante) => (
                        <tr key={vacante.id}>
                            <td>
                                <div className="d-flex flex-column">
                                    <span className="fw-bold">{vacante.titulo}</span>
                                    <small className="text-muted">
                                        {vacante.area || 'Sin área asignada'}
                                    </small>
                                </div>
                            </td>
                            <td>
                                <div className="d-flex align-items-center">
                                    <FaMapMarkerAlt className="text-secondary me-1" />
                                    <span>{vacante.ciudad || 'No especificada'}</span>
                                </div>
                                <small className="text-muted d-block">
                                    {vacante.modalidad === 'presencial' ? 'Presencial' : 
                                     vacante.modalidad === 'remoto' ? 'Remoto' : 
                                     vacante.modalidad === 'hibrido' ? 'Híbrido' : 'No especificado'}
                                </small>
                            </td>
                            <td>
                                <div className="small">
                                    <div className="d-flex align-items-center mb-1">
                                        <FaCalendarAlt className="text-secondary me-1" size={12} />
                                        <span>Creada: {formatearFecha(vacante.fechaCreacion)}</span>
                                    </div>
                                    {vacante.fechaCierre && (
                                        <div className="d-flex align-items-center">
                                            <FaCalendarAlt className="text-danger me-1" size={12} />
                                            <span>Cierre: {formatearFecha(vacante.fechaCierre)}</span>
                                        </div>
                                    )}
                                </div>
                            </td>
                            <td>
                                <Badge 
                                    bg={vacante.estado === 'activa' ? 'success' : 'danger'}
                                    className="px-3 py-2"
                                >
                                    {vacante.estado === 'activa' ? 'Activa' : 'Inactiva'}
                                </Badge>
                            </td>
                            <td className="text-center">
                                <Badge bg="secondary" className="rounded-pill px-3 py-2">
                                    <FaUserTie className="me-1" />
                                    {vacante.cantidadPostulaciones || 0}
                                </Badge>
                            </td>
                            <td>
                                <Dropdown align="end">
                                    <Dropdown.Toggle variant="light" size="sm" id={`dropdown-${vacante.id}`} className="border">
                                        <FaEllipsisV />
                                    </Dropdown.Toggle>
                                    
                                    <Dropdown.Menu>
                                        <Dropdown.Item onClick={() => navigate(`/vacantes/${vacante.id}`)}>
                                            <FaEdit className="me-2 text-primary" /> Editar
                                        </Dropdown.Item>
                                        
                                        <Dropdown.Item 
                                            onClick={() => cambiarEstadoVacante(vacante.id, vacante.estado)}
                                        >
                                            {vacante.estado === 'activa' ? (
                                                <>
                                                    <FaEye className="me-2 text-danger" /> Desactivar
                                                </>
                                            ) : (
                                                <>
                                                    <FaEye className="me-2 text-success" /> Activar
                                                </>
                                            )}
                                        </Dropdown.Item>
                                        
                                        <Dropdown.Divider />
                                        
                                        <Dropdown.Item 
                                            className="text-danger"
                                            onClick={() => eliminarVacante(vacante.id, vacante.titulo)}
                                        >
                                            <FaTrashAlt className="me-2" /> Eliminar
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        );
    };

    return (
        <Container fluid className="py-4">
            <Card className="shadow-sm mb-4">
                <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                    <h4 className="mb-0 d-flex align-items-center">
                        <FaBriefcase className="me-2" />
                        Gestión de Vacantes
                    </h4>
                    <Button 
                        variant="light" 
                        onClick={() => navigate('/vacantes/crear')}
                        className="d-flex align-items-center"
                    >
                        <FaPlus className="me-1" /> Crear Vacante
                    </Button>
                </Card.Header>
                
                <Card.Body>
                    {renderFiltros()}
                    {renderTablaVacantes()}
                </Card.Body>
            </Card>
        </Container>
    );
};

export default VacantesListScreen;